import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getRTTCollaboratorData,
  StateWithEmergencyCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';

/**
 * Re-fetches Collaborator/RTI-downstream call status over REST whenever a
 * call that's already known locally as a collaborator/rti_downstream event
 * is opened. The live share-event websocket push only ever fires once (near
 * call start) and `callEnded` otherwise only updates via a `call_ended`
 * websocket event that an agency which never opened the call while it was
 * active never subscribed to and so silently misses -- this backfills the
 * true status on open, mirroring how the Transcript tab refreshes on open.
 *
 * Gated on `collaborator.type` already being set (rather than just an
 * agency-level capability check) so this never fires for a plain call or an
 * arbitrary/unknown phone number -- `getRTTCollaboratorData`'s fulfilled
 * reducer only writes when the call already exists in the queue, and this
 * gate guarantees that's already true.
 */
const useRefreshCollaboratorStatus = (callerID: string): null => {
  const dispatch = useDispatch();
  const orgName = useCurrentOrgName();
  const collaboratorType = useSelector(
    (state: StateWithEmergencyCalls) =>
      state.emergencyCalls.callQueue[callerID]?.collaborator?.type, // nosemgrep: detect-object-injection
  );

  useEffect(() => {
    if (callerID && orgName && collaboratorType) {
      dispatch(getRTTCollaboratorData({ callerID, orgName }));
    }
  }, [callerID, orgName, collaboratorType, dispatch]);

  return null;
};

export default useRefreshCollaboratorStatus;
